import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { Button, Input, Space, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    SearchOutlined,
} from "@ant-design/icons";

import { ISupport } from "../../../../Support/interfaces";
import { formatDateStringToDateAndTime } from "../../../../../utils/Conversores";
import { removeAcento } from "../../../../../utils/RemoveAcentos";
import { formatPhone } from "../../../../../utils/Masks";
import { ThemeContext } from "../../../../../contexts/ThemeContext";
import useAuth from "../../../../../hooks/useAuth";
import SupportService from "../../../../../services/http/services/SupportService";
import { EmitError } from "../../../../../utils/EmitError";
import { EmitSuccessAlert } from "../../../../../utils/EmitSuccessAlert";
import TableActionButton from "../../../../../components/TableActionButton";
import { IActiveFilterItem } from "../../../../../components/ActiveFiltersBar";
import useTableColumnFilter from "../../../../../hooks/useTableColumnFilter";

export interface ITableData extends ISupport {
    key: number;
}

export interface ITabExternalSupportFilters {
    name: string | null;
    phone: string | null;
    status: string | null;
}

export type SortField = "name" | "status" | "created_at" | null;

interface IUseTabExternalSupportProps {
    origin: "Aplicativo" | "Site";
}

export default function useTabExternalSupport({ origin }: IUseTabExternalSupportProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const { getColumnRadioProps } = useTableColumnFilter();
    const nameInputRef = useRef<any>(null);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [rawData, setRawData] = useState<Array<ITableData>>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });

    const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [supportBeingDeleted, setSupportBeingDeleted] = useState<ITableData | null>(null);

    const [isUpdateModalVisible, setIsUpdateModalVisible] = useState<boolean>(false);
    const [supportBeingUpdated, setSupportBeingUpdated] = useState<ITableData | null>(null);
    const [isLoadingUpdate, setIsLoadingUpdate] = useState<boolean>(false);

    const [filters, setFilters] = useState<ITabExternalSupportFilters>({
        name: null,
        phone: null,
        status: null,
    });

    const [searchName, setSearchName] = useState("");
    const [searchPhone, setSearchPhone] = useState("");

    const [sorter, setSorter] = useState<{ field: SortField; order: "asc" | "desc" | null }>({
        field: "created_at",
        order: "desc",
    });

    const navigate = useNavigate();
    const { activeCity } = useAuth();

    const handleClickViewSupport = useCallback(
        (data: ISupport) => {
            navigate(`/ajuda-e-suporte/externo/${data.id}`);
        },
        [navigate]
    );

    const handleOpenUpdateModal = useCallback((data: ITableData) => {
        setSupportBeingUpdated(data);
        setIsUpdateModalVisible(true);
    }, []);

    const handleCloseUpdateModal = useCallback(() => {
        setIsUpdateModalVisible(false);
        setSupportBeingUpdated(null);
    }, []);

    const handleSaveUpdateSupport = useCallback(
        async (status: string, observation: string) => {
            if (!supportBeingUpdated?.id) return;
            try {
                setIsLoadingUpdate(true);
                const updated = await SupportService.updateStatusSupport(
                    supportBeingUpdated.id,
                    status,
                    observation
                );
                EmitSuccessAlert("Atendimento atualizado com sucesso!");
                setRawData((prev) =>
                    prev.map((item) =>
                        item.id === supportBeingUpdated.id
                            ? { ...item, ...updated, key: updated.id }
                            : item
                    )
                );
                setIsUpdateModalVisible(false);
                setSupportBeingUpdated(null);
            } catch (err) {
                EmitError(err, true, "support_update_error");
            } finally {
                setIsLoadingUpdate(false);
            }
        },
        [supportBeingUpdated]
    );

    const handleDeleteClick = useCallback((data: ITableData) => {
        setSupportBeingDeleted(data);
        setIsDeleteModalVisible(true);
    }, []);

    const handleCancelDelete = useCallback(() => {
        setIsDeleteModalVisible(false);
        setSupportBeingDeleted(null);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!supportBeingDeleted?.id) return;
        try {
            setIsLoadingDelete(true);
            await SupportService.delete(supportBeingDeleted.id);
            EmitSuccessAlert("Mensagem de suporte removida com sucesso!");
            setRawData((prev) => prev.filter((item) => item.id !== supportBeingDeleted.id));
            setIsDeleteModalVisible(false);
            setSupportBeingDeleted(null);
        } catch (err) {
            EmitError(err, true, "support_delete_error");
        } finally {
            setIsLoadingDelete(false);
        }
    }, [supportBeingDeleted]);

    const handleFilterChange = useCallback((key: keyof ITabExternalSupportFilters, value: any) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({
            name: null,
            phone: null,
            status: null,
        });
        setSearchName("");
        setSearchPhone("");
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const stats = useMemo(() => {
        const total = rawData.length;
        const open = rawData.filter((r) => r.status === "Aberto" || r.status === "OPEN").length;
        const closed = rawData.filter((r) => r.status === "Fechado" || r.status === "CLOSED").length;
        const answered = rawData.filter((r) => r.status === "Respondido").length;

        return { total, open, closed, answered };
    }, [rawData]);

    const filteredData = useMemo(() => {
        let result = rawData;

        if (filters.name) {
            const query = removeAcento(filters.name.trim().toLowerCase());
            result = result.filter((item) => {
                const name = item.name ? removeAcento(item.name.toLowerCase()) : "";
                const email = item.email ? item.email.toLowerCase() : "";
                return name.includes(query) || email.includes(query);
            });
        }

        if (filters.phone) {
            const queryDigits = filters.phone.replace(/\D/g, "");
            result = result.filter((item) => {
                const phoneDigits = item.phone ? item.phone.replace(/\D/g, "") : "";
                return queryDigits ? phoneDigits.includes(queryDigits) : true;
            });
        }

        if (filters.status) {
            result = result.filter((item) => item.status === filters.status);
        }

        return result;
    }, [rawData, filters]);

    const sortedData = useMemo(() => {
        if (!sorter.field || !sorter.order) return filteredData;

        return [...filteredData].sort((a, b) => {
            let valA: any = a[sorter.field as keyof ITableData];
            let valB: any = b[sorter.field as keyof ITableData];

            if (sorter.field === "created_at") {
                valA = valA ? new Date(valA).getTime() : 0;
                valB = valB ? new Date(valB).getTime() : 0;
            } else if (typeof valA === "string") {
                valA = removeAcento(valA.toLowerCase());
                valB = removeAcento((valB || "").toLowerCase());
            }

            if (valA < valB) return sorter.order === "asc" ? -1 : 1;
            if (valA > valB) return sorter.order === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredData, sorter]);

    const paginatedData = useMemo(() => {
        const start = (pagination.current - 1) * pagination.pageSize;
        return sortedData.slice(start, start + pagination.pageSize);
    }, [sortedData, pagination]);

    const activeFilterItems: IActiveFilterItem[] = useMemo(() => {
        const items: IActiveFilterItem[] = [];

        if (filters.name) {
            items.push({
                key: "name",
                label: "Nome",
                value: `"${filters.name}"`,
                onRemove: () => {
                    handleFilterChange("name", null);
                    setSearchName("");
                },
            });
        }

        if (filters.phone) {
            items.push({
                key: "phone",
                label: "Telefone",
                value: formatPhone(filters.phone),
                onRemove: () => {
                    handleFilterChange("phone", null);
                    setSearchPhone("");
                },
            });
        }

        if (filters.status) {
            items.push({
                key: "status",
                label: "Status",
                value: filters.status,
                onRemove: () => handleFilterChange("status", null),
            });
        }

        return items;
    }, [filters, handleFilterChange]);

    const handleTableChange = useCallback((newPagination: any, tableFilters: any, newSorter: any) => {
        if (Array.isArray(newSorter)) {
            newSorter = newSorter[0];
        }

        if (newSorter && newSorter.field) {
            const order = newSorter.order === "ascend" ? "asc" : newSorter.order === "descend" ? "desc" : null;
            setSorter({
                field: order ? (newSorter.field as SortField) : null,
                order,
            });
        } else {
            setSorter({ field: null, order: null });
        }
    }, []);

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setPagination({ current: page, pageSize });
    }, []);

    const columns: ColumnsType<ITableData> = useMemo(() => {
        const getSortOrder = (field: SortField): SortOrder | undefined => {
            if (sorter.field !== field) return undefined;
            return sorter.order === "asc" ? "ascend" : sorter.order === "desc" ? "descend" : undefined;
        };

        return [
            {
                title: "Solicitante / Contato",
                dataIndex: "name",
                key: "name",
                sorter: true,
                sortOrder: getSortOrder("name"),
                width: 270,
                filterDropdown: ({ confirm, clearFilters }: any) => (
                    <div style={{ padding: 12, width: 260, display: "flex", flexDirection: "column", gap: 10 }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#ccc" : "#475569", marginBottom: 4 }}>
                                Nome
                            </div>
                            <Input
                                ref={nameInputRef}
                                placeholder="Buscar por nome..."
                                value={searchName}
                                onChange={(e) => setSearchName(e.target.value)}
                                onPressEnter={() => {
                                    confirm();
                                    setFilters((prev) => ({
                                        ...prev,
                                        name: searchName || null,
                                        phone: searchPhone || null,
                                    }));
                                    setPagination((prev) => ({ ...prev, current: 1 }));
                                }}
                                allowClear
                            />
                        </div>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#ccc" : "#475569", marginBottom: 4 }}>
                                Telefone
                            </div>
                            <Input
                                placeholder="(00) 00000-0000"
                                value={searchPhone}
                                onChange={(e) => setSearchPhone(formatPhone(e.target.value))}
                                onPressEnter={() => {
                                    confirm();
                                    setFilters((prev) => ({
                                        ...prev,
                                        name: searchName || null,
                                        phone: searchPhone || null,
                                    }));
                                    setPagination((prev) => ({ ...prev, current: 1 }));
                                }}
                                allowClear
                            />
                        </div>
                        <Space style={{ justifyContent: "space-between", marginTop: 4 }}>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                size="small"
                                onClick={() => {
                                    confirm();
                                    setFilters((prev) => ({
                                        ...prev,
                                        name: searchName || null,
                                        phone: searchPhone || null,
                                    }));
                                    setPagination((prev) => ({ ...prev, current: 1 }));
                                }}
                            >
                                Buscar
                            </Button>
                            <Button
                                size="small"
                                onClick={() => {
                                    if (clearFilters) clearFilters();
                                    setSearchName("");
                                    setSearchPhone("");
                                    setFilters((prev) => ({
                                        ...prev,
                                        name: null,
                                        phone: null,
                                    }));
                                    setPagination((prev) => ({ ...prev, current: 1 }));
                                    confirm();
                                }}
                            >
                                Limpar
                            </Button>
                        </Space>
                    </div>
                ),
                filterIcon: () => (
                    <SearchOutlined style={{ color: searchName || searchPhone || filters.name || filters.phone ? "#1677ff" : undefined }} />
                ),
                render: (_: any, record: ITableData) => (
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <span style={{ fontWeight: 600, color: isDark ? "#f0f0f0" : "#1e293b", fontSize: 13, lineHeight: "1.3", whiteSpace: "nowrap" }}>
                            {filters.name ? (
                                <Highlighter
                                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                                    searchWords={[filters.name]}
                                    autoEscape
                                    textToHighlight={record.name || "--"}
                                />
                            ) : (
                                record.name || "--"
                            )}
                        </span>
                        <span style={{ color: isDark ? "#8c8c8c" : "#64748b", fontSize: 12, fontWeight: 500, letterSpacing: "0.3px", whiteSpace: "nowrap", lineHeight: "1.2" }}>
                            {filters.phone ? (
                                <Highlighter
                                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                                    searchWords={[filters.phone]}
                                    autoEscape
                                    textToHighlight={record.phone ? formatPhone(record.phone) : "--"}
                                />
                            ) : (
                                record.phone ? formatPhone(record.phone) : "--"
                            )}
                        </span>
                    </div>
                ),
            },
            {
                title: "Mensagem",
                dataIndex: "message",
                key: "message",
                render: (val: string) => (
                    <div
                        style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            wordBreak: "break-word",
                            color: isDark ? "#aaa" : "#64748b",
                            fontSize: 12.5,
                            maxWidth: 340,
                        }}
                    >
                        {val || "--"}
                    </div>
                ),
            },
            {
                title: "Atendente",
                dataIndex: "panel_user",
                key: "panel_user",
                width: 150,
                render: (_: any, record: ITableData) =>
                    record.panel_user?.name ? (
                        <span style={{ fontWeight: 500, fontSize: 12.5, color: isDark ? "#93c5fd" : "#1d4ed8", whiteSpace: "nowrap" }}>
                            {record.panel_user.name}
                        </span>
                    ) : (
                        <span style={{ color: isDark ? "#666" : "#94a3b8", fontSize: 12 }}>--</span>
                    ),
            },
            {
                title: "Status",
                dataIndex: "status",
                key: "status",
                width: 120,
                sorter: true,
                sortOrder: getSortOrder("status"),
                ...getColumnRadioProps({
                    title: "Filtrar por Status",
                    value: filters.status || "",
                    onChange: (val) => handleFilterChange("status", val || null),
                    onClear: () => handleFilterChange("status", null),
                    isActive: Boolean(filters.status),
                    options: [
                        { label: "Todos os Status", value: "" },
                        { label: "Aberto", value: "Aberto" },
                        { label: "Respondido", value: "Respondido" },
                        { label: "Fechado", value: "Fechado" },
                    ],
                }),
                render: (val: string) => {
                    if (val === "Fechado" || val === "CLOSED") {
                        return (
                            <Tag color="success" icon={<CheckCircleOutlined />} style={{ borderRadius: 4, fontWeight: 500 }}>
                                Fechado
                            </Tag>
                        );
                    }
                    if (val === "Respondido") {
                        return (
                            <Tag color="blue" icon={<CheckCircleOutlined />} style={{ borderRadius: 4, fontWeight: 500 }}>
                                Respondido
                            </Tag>
                        );
                    }
                    return (
                        <Tag color="warning" icon={<ClockCircleOutlined />} style={{ borderRadius: 4, fontWeight: 500 }}>
                            Aberto
                        </Tag>
                    );
                },
            },
            {
                title: "Data de Cadastro",
                dataIndex: "created_at",
                key: "created_at",
                width: 160,
                sorter: true,
                sortOrder: getSortOrder("created_at"),
                render: (val: string) => (
                    <span style={{ fontSize: 12.5, color: isDark ? "#d9d9d9" : "#475569", whiteSpace: "nowrap" }}>
                        {val ? formatDateStringToDateAndTime(val) : "--"}
                    </span>
                ),
            },
            {
                title: "Ações",
                key: "actions",
                width: 100,
                align: "center" as const,
                render: (_: any, record: ITableData) => (
                    <div
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <TableActionButton
                            action="view"
                            tooltip="Visualizar Detalhes"
                            onClick={() => handleClickViewSupport(record)}
                        />
                        <TableActionButton
                            action="edit"
                            tooltip="Atualizar Status / Observação"
                            onClick={() => handleOpenUpdateModal(record)}
                        />
                        <TableActionButton
                            action="delete"
                            tooltip="Excluir Mensagem"
                            onClick={() => handleDeleteClick(record)}
                        />
                    </div>
                ),
            },
        ];
    }, [
        isDark,
        sorter,
        filters.name,
        filters.phone,
        filters.status,
        searchName,
        searchPhone,
        getColumnRadioProps,
        handleFilterChange,
        handleClickViewSupport,
        handleOpenUpdateModal,
        handleDeleteClick,
    ]);

    const setTableDataFromRequest = useCallback((data: Array<ISupport>) => {
        const newData: ITableData[] = data.map((item) => ({
            ...item,
            key: item.id,
        }));
        setRawData(newData);
    }, []);

    const fetchSupport = useCallback(
        async (controller: AbortSignal, silent = false) => {
            if (!activeCity?.id) return;
            try {
                if (!silent) {
                    setIsLoading(true);
                }
                const filterOrigin = origin === "Aplicativo" ? undefined : origin;
                const list = await SupportService.listSupportMessages({
                    cityId: activeCity.id,
                    origin: filterOrigin,
                    signal: controller,
                });
                setTableDataFromRequest(list);
            } catch (err) {
                if (err instanceof AxiosError && err.name === "CanceledError") {
                    return;
                }
                EmitError(err, true, "support_load_error");
            } finally {
                if (!silent) {
                    setIsLoading(false);
                }
            }
        },
        [activeCity?.id, origin, setTableDataFromRequest]
    );

    const reloadData = useCallback(async () => {
        const controller = new AbortController();
        await fetchSupport(controller.signal, false);
    }, [fetchSupport]);

    useEffect(() => {
        if (!activeCity?.id) return;

        const controller = new AbortController();
        fetchSupport(controller.signal, false);

        return () => {
            controller.abort();
        };
    }, [activeCity?.id, fetchSupport]);

    return {
        isLoading,
        columns,
        tableData: paginatedData,
        totalCount: sortedData.length,
        stats,
        pagination: {
            ...pagination,
            total: sortedData.length,
        },
        activeFilterItems,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        handleClickViewSupport,
        supportBeingDeleted,
        isDeleteModalVisible,
        isLoadingDelete,
        handleCancelDelete,
        handleConfirmDelete,
        supportBeingUpdated,
        isUpdateModalVisible,
        isLoadingUpdate,
        handleOpenUpdateModal,
        handleCloseUpdateModal,
        handleSaveUpdateSupport,
        reloadData,
    };
}
